// Live data sources: a `runtime` source with `refresh` is re-read on an
// interval while the page is visible, and rows that were not there on the
// previous read are reported as NEW (pulse, chime, `onNewRows.action`).
//
// One hook for every live surface. The surface supplies only what it owns —
// how to re-read one source (`refetch`) and where the rows land
// (`resources`, keyed `ds:<id>`) — so the in-app runtime and the downloaded
// runtime poll, diff and announce identically.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { PageDataSource, ResolvedDataEntry } from '@twasta/page-schema'
import { rowsFromEntry } from '../dataBinding'
import { rowKey } from './ids'
import { playChime } from './chime'
import type { LiveRowsState } from './repeatFrame'

export const MIN_REFRESH_SECONDS = 5
export const DEFAULT_REFRESH_SECONDS = 15

/** The poll period of a source in ms, or 0 when it is not live. */
export function refreshPeriodMs(s: PageDataSource | undefined): number {
  if (!s || !s.refresh || s.bindingTime !== 'runtime') return 0
  const n = Number(s.refresh.intervalSeconds)
  const sec = Number.isFinite(n) && n > 0 ? n : DEFAULT_REFRESH_SECONDS
  return Math.max(MIN_REFRESH_SECONDS, sec) * 1000
}

/** Keys present now that were absent before. A first read (no `prev`) is a
 *  baseline, never "new" — opening the screen must not chime for every row. */
export function diffNewKeys(prev: Set<string> | undefined, rows: Record<string, unknown>[], keyColumn?: string): {
  keys: Set<string>
  added: Set<string>
} {
  const keys = new Set<string>()
  for (const r of rows) {
    const k = rowKey(r, keyColumn)
    if (k) keys.add(k)
  }
  const added = new Set<string>()
  if (prev) keys.forEach((k) => { if (!prev.has(k)) added.add(k) })
  return { keys, added }
}

export interface UseLiveSourcesArgs {
  sources: PageDataSource[] | undefined
  /** Rows as the surface holds them, keyed `ds:<id>`. */
  resources: Record<string, unknown>
  /** Re-read one source (the surface merges the result into `resources`). */
  refetch: (sourceId: string) => Promise<unknown> | void
  /** Run a page action by name (for `onNewRows.action`). `{{new_rows}}` = count. */
  runAction?: (name: string, vars: Record<string, unknown>) => void
  /** False to switch polling off (designer canvas, a static render). */
  enabled?: boolean
  /** Extra filter, e.g. skip correlated sources whose rows live per-repeat. */
  include?: (s: PageDataSource) => boolean
}

/** Poll the live sources and track arrivals. Returns the state the shared
 *  `RepeatFrame` reads through `LiveRowsContext`. */
export function useLiveSources({ sources, resources, refetch, runAction, enabled = true, include }: UseLiveSourcesArgs): LiveRowsState {
  const live = useMemo(
    () => (sources || []).filter((s) => refreshPeriodMs(s) > 0 && (!include || include(s))),
    [sources, include],
  )
  const liveKey = live.map((s) => `${s.id}:${refreshPeriodMs(s)}`).join('|')

  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  const runActionRef = useRef(runAction)
  runActionRef.current = runAction
  // Sources whose NEXT read came from a poll. Only those reads announce
  // arrivals: a read caused by the viewer changing a filter, or by their own
  // action's refresh, re-baselines instead of chiming for rows that merely
  // came into view.
  const polled = useRef<Set<string>>(new Set())

  // ── polling ──
  useEffect(() => {
    if (!enabled || !live.length || typeof window === 'undefined') return undefined
    const handles: ReturnType<typeof setInterval>[] = []
    const inFlight = new Set<string>()
    const poll = (id: string) => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      if (inFlight.has(id)) return          // a slow read never stacks up behind itself
      inFlight.add(id)
      polled.current.add(id)
      Promise.resolve()
        .then(() => refetchRef.current(id))
        .catch(() => { /* the surface logs; the next tick retries */ })
        .finally(() => inFlight.delete(id))
    }
    for (const s of live) handles.push(setInterval(() => poll(s.id), refreshPeriodMs(s)))
    // Coming back to a hidden tab: catch up at once instead of waiting a tick.
    const onVisible = () => { if (document.visibilityState === 'visible') live.forEach((s) => poll(s.id)) }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      handles.forEach(clearInterval)
      document.removeEventListener('visibilitychange', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- liveKey summarises `live`
  }, [enabled, liveKey])

  // ── arrivals ──
  const seen = useRef<Record<string, Set<string>>>({})
  const [newKeys, setNewKeys] = useState<Record<string, Set<string>>>({})
  const clearTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const lastEntry = useRef<Record<string, unknown>>({})

  useEffect(() => {
    if (!enabled) return
    for (const s of live) {
      const entry = resources[`ds:${s.id}`] as ResolvedDataEntry | undefined
      // Only a NEW read is judged (the surface replaces the entry object on
      // every fetch); a re-render with the same entry is not an arrival.
      if (!entry || lastEntry.current[s.id] === entry) continue
      lastEntry.current[s.id] = entry
      const rows = rowsFromEntry(entry)
      const keyCol = s.refresh?.keyColumn
      const fromPoll = polled.current.delete(s.id)
      const { keys, added } = diffNewKeys(fromPoll ? seen.current[s.id] : undefined, rows, keyCol)
      seen.current[s.id] = keys
      if (!added.size) continue
      const cfg = s.refresh?.onNewRows || {}
      const secs = cfg.highlightSeconds ?? 6
      if (secs > 0) {
        setNewKeys((prev) => ({ ...prev, [s.id]: new Set([...(prev[s.id] || []), ...added]) }))
        clearTimeout(clearTimers.current[s.id])
        clearTimers.current[s.id] = setTimeout(() => {
          setNewKeys((prev) => {
            const next = { ...prev }
            delete next[s.id]
            return next
          })
        }, secs * 1000)
      }
      if (cfg.chime) playChime()
      if (cfg.action && runActionRef.current) {
        try { runActionRef.current(cfg.action, { new_rows: String(added.size) }) } catch { /* action errors toast themselves */ }
      }
    }
  }, [enabled, resources, live])

  useEffect(() => () => { Object.values(clearTimers.current).forEach(clearTimeout) }, [])

  const highlightSeconds = useMemo(() => {
    const out: Record<string, number> = {}
    for (const s of live) out[s.id] = s.refresh?.onNewRows?.highlightSeconds ?? 6
    return out
  }, [live])

  return useMemo(() => ({ newKeys, highlightSeconds }), [newKeys, highlightSeconds])
}
